import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, MessageSquare, MapPin, Clock, Send, AlertCircle, CheckCircle, Facebook, Twitter, ChevronRight, HelpCircle, Bug, Lightbulb, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const ContactCard = ({ icon: Icon, title, value, subtext, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{ scale: 1.02 }}
    className="bg-white/5 border border-white/10 rounded-xl p-6"
  >
    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${color}20` }}>
      <Icon style={{ color }} size={24} />
    </div>
    <h3 className="font-bebas text-xl text-white tracking-wider mb-1">{title}</h3>
    <p className="text-marigold font-inter">{value}</p>
    {subtext && <p className="text-gray-500 text-sm font-inter mt-1">{subtext}</p>}
  </motion.div>
);

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      className="border-b border-white/10"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left"
      >
        <span className="font-bebas text-lg text-white tracking-wider">{question}</span>
        <ChevronRight className={`text-marigold transition-transform ${isOpen ? 'rotate-90' : ''}`} size={20} />
      </button>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="pb-4"
        >
          <p className="text-gray-400 font-inter text-sm leading-relaxed">{answer}</p>
        </motion.div>
      )}
    </motion.div>
  );
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "general",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setSubmitted(true);
    toast.success("Message sent! We'll get back to you soon.");
    
    // Reset form after delay
    setTimeout(() => {
      setFormData({ name: "", email: "", subject: "general", message: "" });
      setSubmitted(false);
    }, 3000);
  };

  const faqs = [
    {
      question: "Is this an official government election platform?",
      answer: "No, Nepali Ballot is an independent simulation platform for educational and entertainment purposes only. We are not affiliated with the Election Commission of Nepal or any government body. Votes cast here have no legal effect."
    },
    {
      question: "Where do you get your candidate information?",
      answer: "We aggregate data from trusted sources including the Election Commission of Nepal, major news outlets like Kathmandu Post and BBC Nepali, and verified public records. All information is cross-referenced for accuracy."
    },
    {
      question: "How can I report incorrect information?",
      answer: "Please use the contact form above or email us at buildersadventure00@gmail.com with details about the inaccuracy. We take data accuracy seriously and will review and correct any errors promptly."
    },
    {
      question: "Can I use your data for research or journalism?",
      answer: "Yes, our aggregated and publicly available data can be used for non-commercial research and journalism purposes with proper attribution to Nepali Ballot. For commercial use, please contact us first."
    },
    {
      question: "Is my voting data private?",
      answer: "Yes, all votes are anonymous. We do not collect personally identifiable information linked to votes. See our Privacy Policy for complete details on data handling."
    }
  ];

  return (
    <div className="min-h-screen bg-night-count">
      {/* Hero Section */}
      <div className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-72 h-72 bg-marigold/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-10 left-10 w-72 h-72 bg-peace-blue/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Mail className="mx-auto mb-6 text-marigold" size={64} />
            <h1 className="font-bebas text-6xl sm:text-7xl text-white tracking-wider mb-4" style={{ textShadow: "0 0 60px rgba(247,127,0,0.5)" }}>
              CONTACT US
            </h1>
            <p className="font-playfair text-xl text-gray-300 italic">
              "We'd love to hear from you - questions, feedback, or suggestions"
            </p>
          </motion.div>
        </div>
      </div>

      {/* Contact Cards */}
      <div className="max-w-6xl mx-auto px-4 -mt-8">
        <div className="grid md:grid-cols-3 gap-6">
          <ContactCard 
            icon={Mail}
            title="EMAIL US"
            value="buildersadventure00@gmail.com"
            subtext="We respond within 24-48 hours"
            color="#F77F00"
          />
          <ContactCard 
            icon={Clock}
            title="RESPONSE TIME"
            value="24-48 Hours"
            subtext="Monday - Friday"
            color="#2563EB"
          />
          <ContactCard 
            icon={MapPin}
            title="BASED IN"
            value="Nepal"
            subtext="Serving globally"
            color="#D90429"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-bebas text-3xl text-white tracking-wider mb-6 flex items-center gap-3">
              <MessageSquare className="text-marigold" size={28} />
              SEND US A MESSAGE
            </h2>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-congress-green/20 border border-congress-green/40 rounded-xl p-8 text-center"
              >
                <CheckCircle className="mx-auto mb-4 text-congress-green" size={48} />
                <h3 className="font-bebas text-2xl text-white tracking-wider mb-2">MESSAGE SENT!</h3>
                <p className="text-gray-400 font-inter">Thank you for reaching out. We'll get back to you soon.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 font-inter mb-1 block">Your Name *</label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ram Bahadur"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 font-inter mb-1 block">Email Address *</label>
                    <Input
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="ram@example.com"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-400 font-inter mb-1 block">Subject *</label>
                  <select
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white rounded-md px-3 py-2 font-inter text-sm"
                  >
                    <option value="general" className="bg-night-count">General Inquiry</option>
                    <option value="feedback" className="bg-night-count">Feedback & Suggestions</option>
                    <option value="bug" className="bg-night-count">Report a Bug</option>
                    <option value="data" className="bg-night-count">Data Correction Request</option>
                    <option value="partnership" className="bg-night-count">Partnership Inquiry</option>
                    <option value="media" className="bg-night-count">Media & Press</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm text-gray-400 font-inter mb-1 block">Message *</label>
                  <textarea
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="How can we help you?"
                    className="w-full bg-white/5 border border-white/10 text-white rounded-md px-3 py-2 font-inter text-sm placeholder:text-gray-500 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-marigold hover:bg-marigold/80 text-peace-blue font-bebas text-lg tracking-wider py-6"
                >
                  {isSubmitting ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity }}
                        className="w-5 h-5 border-2 border-peace-blue border-t-transparent rounded-full mr-2"
                      />
                      SENDING...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={18} />
                      SEND MESSAGE
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>

          {/* Quick Contact Options */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-bebas text-3xl text-white tracking-wider mb-6 flex items-center gap-3">
              <HelpCircle className="text-peace-blue" size={28} />
              QUICK HELP
            </h2>

            <div className="space-y-4 mb-8">
              {[
                { icon: Bug, title: "Report a Bug", desc: "Found something broken?", email: "buildersadventure00@gmail.com" },
                { icon: AlertCircle, title: "Data Correction", desc: "Incorrect information?", email: "buildersadventure00@gmail.com" },
                { icon: Lightbulb, title: "Feature Request", desc: "Have an idea?", email: "buildersadventure00@gmail.com" },
                { icon: Users, title: "Partnership", desc: "Work with us", email: "buildersadventure00@gmail.com" },
              ].map((item, i) => (
                <motion.a
                  key={i}
                  href={`mailto:${item.email}`}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl p-4 hover:border-marigold/50 transition-all group"
                >
                  <div className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center group-hover:bg-marigold/20 transition-colors">
                    <item.icon className="text-gray-400 group-hover:text-marigold transition-colors" size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-bebas tracking-wider">{item.title}</p>
                    <p className="text-gray-500 text-xs font-inter">{item.desc}</p>
                  </div>
                  <p className="text-marigold text-sm font-inter hidden md:block">{item.email}</p>
                </motion.a>
              ))}
            </div>

            {/* Social Links */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="font-bebas text-xl text-white tracking-wider mb-4">FOLLOW US</h3>
              <div className="flex gap-4">
                <a href="#" className="w-12 h-12 bg-[#1877F2]/20 rounded-xl flex items-center justify-center hover:bg-[#1877F2]/40 transition-colors">
                  <Facebook className="text-[#1877F2]" size={24} />
                </a>
                <a href="#" className="w-12 h-12 bg-[#1DA1F2]/20 rounded-xl flex items-center justify-center hover:bg-[#1DA1F2]/40 transition-colors">
                  <Twitter className="text-[#1DA1F2]" size={24} />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-black/30 py-16">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <HelpCircle className="mx-auto mb-4 text-marigold" size={40} />
            <h2 className="font-bebas text-4xl text-white tracking-wider mb-2">FREQUENTLY ASKED QUESTIONS</h2>
            <p className="text-gray-400 font-inter">Quick answers to common questions</p>
          </motion.div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            {faqs.map((faq, i) => (
              <FAQItem key={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>

          <div className="text-center mt-6">
            <p className="text-gray-400 font-inter text-sm">
              Can't find what you're looking for?{" "}
              <a href="mailto:buildersadventure00@gmail.com" className="text-marigold hover:underline">Email our support team</a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <Link to="/privacy" className="text-gray-400 hover:text-marigold text-sm font-inter">Privacy Policy</Link>
            <Link to="/terms" className="text-gray-400 hover:text-marigold text-sm font-inter">Terms of Service</Link>
            <Link to="/disclaimer" className="text-gray-400 hover:text-marigold text-sm font-inter">Disclaimer</Link>
            <Link to="/about" className="text-gray-400 hover:text-marigold text-sm font-inter">About Us</Link>
          </div>
          <p className="text-center text-gray-500 text-sm">© 2026 Nepali Ballot • All Rights Reserved</p>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
